import { useEffect, useState } from 'react';

import { Backdrop, CircularProgress } from '@mui/material';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';

import NoResourcePage from '../../components/NoResourcePage';
import useApp from '../../hooks/useApp';
import useTeam from '../../hooks/useTeam';
import { redirectToAuth0 } from '../../lib/auth0';
import { getErrorMessage } from '../../lib/error';
import { hasRegistered, isEmailVerifyPending, isValidJwt } from '../../lib/jwt';
import Routes from '../../lib/routes';
import { getSDKClient } from '../../lib/sdk';
import storage from '../../lib/storage';

type Params = {
  inviteCode: string;
};

const inviteErrorDetailsToDisplayedMessage: Record<string, string> = {
  'team invite has expired': 'This invitation has expired. Ask a team owner/manager to send a new one.',
  'team invite has already been used': 'This invitation has already been used.',
  'unable to accept team invite':
    'This invitation cannot be used with this account. Sign in with the email that received the invitation.',
};

export default function AcceptTeamInvite(): JSX.Element {
  const { inviteCode } = useParams<Params>();
  const { app, setApp } = useApp();
  const { refreshTeamsThenSet } = useTeam();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [joinedTeamName, setJoinedTeamName] = useState<string | null>(null);

  const env = app.env;
  const token = app.tokens.get(env) ?? '';

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setErrorMessage(null);
    setJoinedTeamName(null);

    // Save this invite URL as nextRedirect. After Auth0 login, CallbackV2
    // sends registered users here.
    // Write localStorage immediately: Auth0 leaves the page, so setApp may
    // not flush in time. CallbackV2 reads nextRedirect from AppContext, which reloads
    // it from storage on boot.
    const returnPath = `/invite/${encodeURIComponent(inviteCode)}`;
    const stashReturnPath = () => {
      storage.set('nextRedirect', returnPath);
      setApp((prev) => ({ ...prev, nextRedirect: returnPath }));
    };

    // If the user is not logged into Auth0, or their email is not verified,
    // redirect the user to Auth0, and then send them back here.
    if (!isValidJwt(token) || isEmailVerifyPending(token)) {
      stashReturnPath();
      redirectToAuth0();
      return;
    }

    // If the user is logged into Auth0, but hasn't finished developer registration,
    // redirect the user to the registration form, and then send them back here.
    if (!hasRegistered(token)) {
      stashReturnPath();
      history.push(Routes.Register);
      return;
    }

    // If the user is logged into Auth0 and has finished developer registration,
    // accept the invite and select that team.
    const accept = async () => {
      try {
        const rsp = await getSDKClient(env, token).acceptTeamInvite(inviteCode);
        if (cancelled) {
          return;
        }
        const joinedTeam = await refreshTeamsThenSet(rsp.team_id);
        if (cancelled) {
          return;
        }
        setJoinedTeamName(joinedTeam?.team_name ?? null);
        setIsLoading(false);
      } catch (err) {
        if (cancelled) {
          return;
        }
        let message = getErrorMessage(err);
        if (axios.isAxiosError(err)) {
          const details = err.response?.data?.error?.details;
          if (typeof details === 'string') {
            const mapped = inviteErrorDetailsToDisplayedMessage[details];
            if (typeof mapped === 'string') {
              message = mapped;
            }
          }
        }
        setErrorMessage(message);
        setIsLoading(false);
      }
    };

    accept();
    return () => {
      cancelled = true;
    };
  }, [env, history, inviteCode, refreshTeamsThenSet, setApp, token]);

  if (isLoading) {
    return (
      <Backdrop open={true}>
        <CircularProgress
          size={64}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-12px',
            marginLeft: '-12px',
          }}
        />
      </Backdrop>
    );
  }

  if (errorMessage !== null) {
    return (
      <NoResourcePage
        title="Unable to accept invitation"
        subtitle={errorMessage}
        buttonText="Go to dashboard"
        buttonHref={Routes.Overview}
      />
    );
  }

  return (
    <NoResourcePage
      title="Invitation accepted"
      subtitle={joinedTeamName !== null ? `You have joined ${joinedTeamName}.` : 'You have joined the team.'}
      buttonText="Go to dashboard"
      buttonHref={Routes.Overview}
    />
  );
}
